import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Monitor } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useAuthStore } from "@/store/auth";
import { getOnlineUsers } from "../api/screenshareApi";
import { isScreenShareViewer } from "../eligibility";

interface Props {
  open: boolean;
  onClose: () => void;
  /** view_request — ask to watch the picked user's screen. */
  onStartView: (targetId: string) => void;
  /** share_offer — show my screen to the picked (privileged) user. */
  onStartShare: (targetId: string) => void;
}

type Mode = "view" | "share";

const tabCls = (active: boolean) =>
  `flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
    active
      ? "bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
      : "text-[var(--color-text-secondary)] hover:bg-black/5 dark:hover:bg-white/5"
  }`;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function ScreenSharePicker({ open, onClose, onStartView, onStartShare }: Props) {
  const me = useAuthStore((s) => s.user);
  const canView = isScreenShareViewer(me);
  const [mode, setMode] = useState<Mode>(canView ? "view" : "share");
  // The online roster (names + role only — no profile pictures). Self excluded
  // server-side. Refresh while the picker is open.
  const { data: online = [], isLoading } = useQuery({
    queryKey: ["screenshare-online"],
    queryFn: getOnlineUsers,
    enabled: open,
    staleTime: 10_000,
    refetchInterval: open ? 15_000 : false,
  });

  const list = useMemo(
    // Sharing means picking a viewer → only privileged online users.
    () => (mode === "share" ? online.filter((u) => u.is_viewer) : online),
    [online, mode],
  );

  const pick = (id: string) => {
    if (mode === "view") onStartView(id);
    else onStartShare(id);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Podgląd ekranu" size="sm">
      {canView && (
        <div className="mb-4 flex gap-1 rounded-lg border border-[var(--color-border)] p-1">
          <button type="button" onClick={() => setMode("view")} className={tabCls(mode === "view")}>
            <Eye size={13} /> Podejrzyj czyjś ekran
          </button>
          <button type="button" onClick={() => setMode("share")} className={tabCls(mode === "share")}>
            <Monitor size={13} /> Pokaż swój ekran
          </button>
        </div>
      )}
      <p className="mb-3 text-xs text-[var(--color-text-muted)]">
        {mode === "view"
          ? "Wybierz osobę online — dostanie prośbę o zgodę na podgląd jej ekranu."
          : "Wybierz osobę online, której pokażesz ekran. Następnie wskażesz okno/kartę w przeglądarce."}
      </p>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-[var(--color-text-muted)]">Ładowanie…</div>
      ) : list.length === 0 ? (
        <div className="py-6 text-center text-sm text-[var(--color-text-muted)]">
          {mode === "share" ? "Brak osób online, którym możesz pokazać ekran." : "Nikt nie jest teraz online."}
        </div>
      ) : (
        <ul className="max-h-80 space-y-1 overflow-y-auto">
          {list.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => pick(u.id)}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-[var(--color-primary)] text-[10px] font-bold text-[var(--color-text-inverse)]">
                  {initials(u.name)}
                </span>
                <span className="min-w-0 flex-1 truncate text-[var(--color-text-primary)]">{u.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
